import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { fetchGeolocation, submitLead, getCountries, findCountryByCode } from '../services/leadService';
import { validateEmail, validatePhoneNumber, validateName } from '../utils/validation';

const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '1',
    country: 'US',
    contactNumber: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [geoLocationData, setGeoLocationData] = useState(null);
  const [countries] = useState(getCountries());

  // Fetch geolocation when popup opens
  useEffect(() => {
    if (isOpen && !geoLocationData) {
      const loadGeolocation = async () => {
        const geoData = await fetchGeolocation();
        if (geoData && geoData.isSuccess && geoData.data) {
          setGeoLocationData(geoData);
          
          // Auto-fill country based on geolocation
          const detectedCountry = findCountryByCode(geoData.data.countryCode);
          if (detectedCountry) {
            setFormData(prev => ({
              ...prev,
              country: detectedCountry.code,
              countryCode: detectedCountry.dialCode
            }));
          }
        }
      };
      
      loadGeolocation();
    }
  }, [isOpen, geoLocationData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    const nameValidation = validateName(formData.name);
    if (!nameValidation.isValid) {
      setError(nameValidation.error);
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return;
    }

    const phoneValidation = validatePhoneNumber(formData.contactNumber, formData.countryCode);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.error);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Submit lead using centralized API service
      const result = await submitLead(
        {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.contactNumber,
          countryDialCode: formData.countryCode,
          country: formData.country,
          description: formData.description
        },
        geoLocationData
      );

      if (result.success && result.customerId) {
        // Close popup and redirect
        onClose();
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        // Show error message
        setError(result.error || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleCountryCodeChange = (value) => {
    const country = countries.find(c => c.dialCode === value);
    if (country) {
      setFormData(prev => ({
        ...prev,
        countryCode: value,
        country: country.code
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @media (max-width: 991px) {
          .left-tab {
            display: none !important;
          }
        }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-6 h-6 text-gray-600" />
        </button>

        <div className="grid md:grid-cols-2 h-full">
          {/* Left Side - Image */}
          <div className="relative bg-gradient-to-br from-brand-purple to-brand-pink p-8 flex items-center justify-center left-tab">
            <div className="text-center text-white">
              <div className="mb-6">
                <svg className="w-full h-80 mx-auto" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Consultation/Meeting Illustration */}
                  <circle cx="200" cy="200" r="150" fill="rgba(255,255,255,0.1)"/>
                  <circle cx="200" cy="200" r="120" fill="rgba(255,255,255,0.15)"/>
                  
                  {/* Laptop */}
                  <rect x="120" y="180" width="160" height="100" rx="8" fill="white" opacity="0.9"/>
                  <rect x="130" y="190" width="140" height="70" fill="#1E40AF"/>
                  <rect x="110" y="280" width="180" height="8" rx="4" fill="white" opacity="0.8"/>
                  
                  {/* Person 1 */}
                  <circle cx="150" cy="140" r="25" fill="white"/>
                  <path d="M125 165 Q150 175 175 165" stroke="white" strokeWidth="20" fill="white"/>
                  
                  {/* Person 2 */}
                  <circle cx="250" cy="140" r="25" fill="white"/>
                  <path d="M225 165 Q250 175 275 165" stroke="white" strokeWidth="20" fill="white"/>
                  
                  {/* Documents/Papers */}
                  <rect x="90" y="220" width="40" height="50" rx="4" fill="white" opacity="0.7"/>
                  <rect x="270" y="220" width="40" height="50" rx="4" fill="white" opacity="0.7"/>
                  
                  {/* Checkmarks */}
                  <path d="M100 240 L108 248 L120 232" stroke="#10B981" strokeWidth="3" fill="none"/>
                  <path d="M280 240 L288 248 L300 232" stroke="#10B981" strokeWidth="3" fill="none"/>
                  
                  {/* Chat bubbles */}
                  <circle cx="320" cy="100" r="30" fill="white" opacity="0.8"/>
                  <circle cx="80" cy="100" r="25" fill="white" opacity="0.8"/>
                  <text x="320" y="110" textAnchor="middle" fill="#1E40AF" fontSize="24">💬</text>
                  <text x="80" y="105" textAnchor="middle" fill="#1E40AF" fontSize="20">✓</text>
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-4">Let's Get Started!</h2>
              <p className="text-lg opacity-90">
                Fill out the form and our team will contact you shortly to discuss your needs.
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-8 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get in Touch</h3>
              <p className="text-gray-600">We're here to help with your trademark needs</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>

              {/* Contact Number with Country Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Number * {geoLocationData && `(${geoLocationData.data.countryName})`}
                </label>
                <div className="flex gap-2">
                  <Select
                    value={formData.countryCode}
                    onValueChange={handleCountryCodeChange}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Code" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.code} value={country.dialCode}>
                          +{country.dialCode} ({country.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    placeholder="1234567890"
                    required
                    disabled={isSubmitting}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How can we help you?
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your trademark needs..."
                  disabled={isSubmitting}
                  rows={4}
                  className="w-full resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white py-6 text-lg disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              By submitting this form, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactPopup;
